import type { AddendaDetails, NormalizedTable, ParcelRecord } from './types';

export type AddendaEntry = {
  key: string;
  fields: Record<string, string>;
  summary: string;
};

function normalizedHeader(value: string) {
  return value.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function headerIndex(table: NormalizedTable, names: string[]) {
  const normalized = table.headers.map(normalizedHeader);
  return (
    names
      .map(normalizedHeader)
      .map((name) => normalized.indexOf(name))
      .find((index) => index >= 0) ?? -1
  );
}

function rowValue(table: NormalizedTable, row: string[], names: string[]) {
  const index = headerIndex(table, names);
  return index >= 0 ? (row[index] ?? '') : '';
}

export function isAddendaTable(table: NormalizedTable) {
  const headers = new Set(table.headers.map(normalizedHeader));
  return (
    /addenda/i.test(table.title) ||
    (headers.has('station') && headers.has('checkedby') && headers.has('holddescription'))
  );
}

function dateValue(value: string) {
  const [month = NaN, day = NaN, rawYear = NaN] = value.split('/').map(Number);
  const year = rawYear < 100 ? (rawYear < 70 ? 2000 + rawYear : 1900 + rawYear) : rawYear;
  const timestamp = Date.UTC(year, month - 1, day);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function latestDate(values: string[]) {
  const dates = values
    .flatMap((value) => value.match(/\b\d{1,2}\/\d{1,2}\/(?:\d{2}|\d{4})\b/g) ?? [])
    .sort((a, b) => dateValue(b) - dateValue(a));
  return dates[0] ?? '';
}

function activityDate(table: NormalizedTable, row: string[]) {
  const description = rowValue(table, row, ['Hold Description']);
  const firstDatedNote = description.match(/\b\d{1,2}\/\d{1,2}\/(?:\d{2}|\d{4})\b/);
  if (firstDatedNote) return firstDatedNote[0];
  return latestDate(row.filter((_, index) => index !== headerIndex(table, ['Hold Description'])));
}

function entrySummary(table: NormalizedTable, row: string[]) {
  const station = rowValue(table, row, ['Station']);
  const date = activityDate(table, row);
  const description = rowValue(table, row, ['Hold Description']);
  return (
    [station, date, description].filter(Boolean).join(' · ') || row.filter(Boolean).join(' · ')
  );
}

export function addendaEntries(record: ParcelRecord): AddendaEntry[] {
  const occurrences = new Map<string, number>();
  const entries: AddendaEntry[] = [];

  for (const page of record.pages) {
    for (const table of page.tables) {
      if (!isAddendaTable(table)) continue;
      for (const row of table.rows) {
        const fields = Object.fromEntries(
          table.headers.map((header, index) => [header || `Column ${index + 1}`, row[index] ?? '']),
        );
        const station = rowValue(table, row, ['Station']) || 'Entry';
        const occurrence = (occurrences.get(station) ?? 0) + 1;
        occurrences.set(station, occurrence);
        entries.push({
          key: `${station} · ${occurrence}`,
          fields,
          summary: entrySummary(table, row),
        });
      }
    }
  }

  return entries;
}

export function addendaDetails(record: ParcelRecord): AddendaDetails[] {
  return record.pages
    .flatMap((page) => page.tables.filter(isAddendaTable))
    .flatMap((table) =>
      table.rows
        .filter((row) => row.some(Boolean))
        .map((row) => ({
          station: rowValue(table, row, ['Station']),
          date: activityDate(table, row),
          checkedBy: rowValue(table, row, ['Checked By']),
          reviewResult: rowValue(table, row, ['Review Result']),
          holdDescription: rowValue(table, row, ['Hold Description']),
          sourceUrl: record.sourceUrl,
        })),
    )
    .reverse();
}
